import { useState, useRef, useEffect } from 'react';
import { SearchIcon, FilterIcon, HeartIcon, GownIcon } from '../components/Icons';

const SORT_OPTIONS = ['Default', 'Price: Low to High', 'Price: High to Low', 'Name A-Z'];

function ProductCard({ product, wishlist, onWishlist, onNavigate }) {
  const isWished = wishlist.includes(product.id);
  const coverImage = product.images?.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) || product.image;

  return (
    <div
      onClick={() => onNavigate('productdetail', { product })}
      className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-0.5"
    >
      <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden">
        {coverImage ? (
          coverImage.match(/\.(mp4|webm|ogg)$/i) ? (
            <video src={coverImage} autoPlay muted loop playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <img src={coverImage} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => { e.target.style.display = 'none'; }} />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GownIcon className="w-10 h-10 text-gray-200" />
          </div>
        )}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none">
            <svg className="w-14 h-14 text-red-500/90 drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        )}
        {product.stock === 0 && <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full z-20">Sold Out</div>}
        {product.stock !== undefined && product.stock > 0 && (
          <div className="absolute top-2 left-2 bg-amber-500/90 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full">{product.stock} left</div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onWishlist(product.id); }}
          className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm transition-all shadow-sm ${
            isWished ? 'bg-[#F47B20] text-white' : 'bg-white/80 text-gray-400 hover:text-[#F47B20] hover:bg-white'
          }`}
        >
          <HeartIcon className="w-3.5 h-3.5" filled={isWished} />
        </button>
      </div>
      <div className="px-3 pt-2.5 pb-3">
        <p className="text-[#1A1A2E] text-[11px] font-medium leading-snug line-clamp-2 mb-1 tracking-tight">{product.name}</p>
        {product.description ? <p className="text-gray-400 text-[10px] leading-snug line-clamp-1 mb-1.5">{product.description}</p> : null}
        <div className="flex items-baseline gap-1.5">
          <p className="font-bold text-[#1A1A2E] text-sm">{product.priceStr || `₦${Number(product.price || 0).toLocaleString()}`}</p>
          {product.original_price && product.original_price > product.price ? (
            <p className="text-gray-400 text-[10px] line-through">{`₦${Number(product.original_price).toLocaleString()}`}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DesktopProductCard({ product, wishlist, onWishlist, onNavigate }) {
  const isWished = wishlist.includes(product.id);
  const outOfStock = product.stock === 0;
  const coverImage = product.images?.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) || product.image;
  return (
    <div
      onClick={() => onNavigate('productdetail', { product })}
      className={`group relative bg-white rounded-xl overflow-hidden cursor-pointer border border-gray-100 hover:shadow-[0_4px_20px_rgba(0,0,0,0.1)] hover:border-[#F47B20]/30 transition-all duration-200 ${outOfStock ? 'opacity-80' : ''}`}
    >
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {coverImage ? (
          coverImage.match(/\.(mp4|webm|ogg)$/i) ? (
            <video src={coverImage} autoPlay muted loop playsInline className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <img src={coverImage} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={e => { e.target.style.display = 'none'; }} />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GownIcon className="w-8 h-8 text-gray-200" />
          </div>
        )}
        {outOfStock && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none">
            <svg className="w-10 h-10 text-red-500/90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        )}
        {outOfStock && <div className="absolute top-1.5 left-1.5 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full z-20">Sold Out</div>}
        {!outOfStock && product.stock > 0 && product.stock <= 5 && (
          <div className="absolute top-1.5 left-1.5 bg-amber-500/90 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">{product.stock} left</div>
        )}
        <button
          onClick={e => { e.stopPropagation(); onWishlist(product.id); }}
          className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center backdrop-blur-sm transition-all shadow-sm opacity-0 group-hover:opacity-100 ${
            isWished ? 'bg-[#F47B20] text-white opacity-100' : 'bg-white/80 text-gray-400 hover:text-[#F47B20]'
          }`}
        >
          <HeartIcon className="w-3 h-3" filled={isWished} />
        </button>
      </div>
      <div className="p-2.5">
        <p className="text-[#1A1A2E] text-[11px] leading-snug line-clamp-2 mb-1 font-medium">{product.name}</p>
        {product.description ? <p className="text-gray-400 text-[10px] leading-snug line-clamp-1 mb-1">{product.description}</p> : null}
        <div className="flex items-baseline gap-1.5">
          <p className="font-black text-[#F47B20] text-sm">{product.priceStr || `₦${Number(product.price || 0).toLocaleString()}`}</p>
          {product.original_price && product.original_price > product.price ? (
            <p className="text-gray-400 text-[10px] line-through">{`₦${Number(product.original_price).toLocaleString()}`}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function SearchPage({ query, products, onNavigate, onToggleWishlist, wishlistIds }) {
  const [searchQuery, setSearchQuery] = useState(query || '');
  const [showFilter, setShowFilter] = useState(false);
  const [sortBy, setSortBy] = useState('Default');
  const filterRef = useRef(null);

  useEffect(() => {
    setSearchQuery(query || '');
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredProducts = (products || []).filter(p => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q);
    return matchesSearch && p.status !== 'draft';
  });

  const displayedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'Price: Low to High') return (a.price || 0) - (b.price || 0);
    if (sortBy === 'Price: High to Low') return (b.price || 0) - (a.price || 0);
    if (sortBy === 'Name A-Z') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  return (
    <div className="bg-[#FAF3F0] min-h-screen">
      {/* ════ MOBILE LAYOUT (hidden on lg+) ════ */}
      <div className="lg:hidden">
        <div className="px-4 pt-3 pb-2.5">
          <div className="flex gap-2 items-center">
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input 
                id="search-page-mobile" 
                className="w-full pl-8 pr-3 py-2 rounded-xl bg-white border border-gray-100 text-sm outline-none focus:border-[#F47B20] shadow-sm placeholder-gray-400" 
                placeholder="Search..." 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)} 
              />
            </div>
            <div className="relative" ref={filterRef}>
              <button 
                id="filter-btn-mobile" 
                onClick={() => setShowFilter(v => !v)} 
                className={`flex items-center gap-1 px-2.5 py-2 rounded-xl border text-xs font-medium transition-colors shadow-sm ${showFilter ? 'bg-[#F47B20] text-white border-[#F47B20]' : 'bg-white border-gray-100 text-gray-600 hover:border-[#F47B20]'}`}
              >
                <FilterIcon className="w-3.5 h-3.5" /><span>Sort</span>
              </button>
              {showFilter && (
                <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                  <div className="p-2.5">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 px-1">Sort by</p>
                    {SORT_OPTIONS.map(opt => (
                      <button key={opt} onClick={() => { setSortBy(opt); setShowFilter(false); }} className={`w-full text-left text-xs px-3 py-2 rounded-xl transition-colors ${sortBy === opt ? 'bg-[#FEF0E6] text-[#F47B20] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>{opt}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="px-4 pb-28 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#1A1A2E]">{searchQuery ? `Results for "${searchQuery}"` : 'All Products'}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {displayedProducts.length > 0 ? displayedProducts.map(product => (
              <ProductCard key={product.id} product={product} wishlist={wishlistIds} onWishlist={onToggleWishlist} onNavigate={onNavigate} />
            )) : (
              <div className="col-span-full text-center py-16 bg-white rounded-3xl border border-gray-100 shadow-sm mt-4">
                <SearchIcon className="w-7 h-7 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-semibold text-sm">No products found</p>
                <button onClick={() => setSearchQuery('')} className="mt-4 text-sm px-6 py-2 bg-[#FEF0E6] text-[#F47B20] rounded-xl font-bold">Clear search</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════ DESKTOP LAYOUT ════ */}
      <div className="hidden lg:block max-w-[1400px] mx-auto px-6 mt-5 pb-12 flex gap-5 items-start">
        {/* Filter sidebar */}
        <div className="w-48 flex-shrink-0 sticky top-[130px] space-y-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3.5">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5">Search</h4>
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input className="w-full pl-6 pr-2 py-1.5 rounded-lg bg-gray-50 border border-gray-100 text-[11px] outline-none focus:border-[#F47B20] placeholder-gray-400" placeholder="Search…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3.5">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5">Sort By</h4>
            <div className="space-y-0.5">
              {SORT_OPTIONS.map(opt => (
                <button key={opt} onClick={() => setSortBy(opt)} className={`w-full text-left text-[11px] px-2 py-1.5 rounded-lg transition-all ${sortBy === opt ? 'bg-[#FEF0E6] text-[#F47B20] font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>{opt}</button>
              ))}
            </div>
          </div>
        </div>

        {/* 5-column grid */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-[#1A1A2E]">
              {searchQuery ? `Search Results for "${searchQuery}"` : 'All Products'}
              <span className="ml-2 text-xs font-normal text-gray-400">({displayedProducts.length} items)</span>
            </h2>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {displayedProducts.length > 0 ? displayedProducts.map(product => (
              <DesktopProductCard key={product.id} product={product} wishlist={wishlistIds} onWishlist={onToggleWishlist} onNavigate={onNavigate} />
            )) : (
              <div className="col-span-full text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <SearchIcon className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-semibold">No products found</p>
                <button onClick={() => setSearchQuery('')} className="mt-4 px-6 py-2 bg-[#FEF0E6] text-[#F47B20] rounded-xl font-bold text-sm hover:bg-[#F47B20] hover:text-white transition-colors">Clear search</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
